import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import { Alert, AppBar, Box, Button, ButtonBase, Stack, Toolbar, Typography } from '@mui/material';
// import { loadCaptchaEnginge } from 'react-simple-captcha';
// hooks
import { m } from 'framer-motion';
import { useNavigate, useParams } from 'react-router';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import LoginSignupDialog from './visitor/LoginSignupDialog';
import ForgotPasswordDialog from './forgotpassword/ForgotPasswordDialog';
import ResetPasswordDialog from './forgotpassword/ResetPasswordDialog';
import { useDispatch, useSelector } from '../../../redux/store';
import { openLoginSignup } from '../../../redux/slices/authPages';
import { openTransactionsDialog } from '../../../redux/slices/transactionsDialog';

import Image from '../../../components/Image';
import NotificationPopover from './NotificationPopover';
import AccountPopover from './AccountPopover';
import useLocales from '../../../hooks/useLocales';
import { fNumber } from '../../../utils/formatNumber';
import palette from '../../../theme/palette';
import useAuth from '../../../hooks/useAuth';
import LanguagePopover from './LanguagePopover';
import { GoldBorderButton, GradientBorderWrapper, ShiningGoldBorder } from '../../../components/settings/GlobalStyled';
import DownloadAppAlert from './DownloadAppAlert';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ theme, showDownloadApp, isDesktop }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: '0px 1px 5px 5px rgb(0, 0, 0, 0.2)',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  top: showDownloadApp && !isDesktop ? 36 : 0,
  backgroundColor: 'transparent',
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  // [theme.breakpoints.up('lg')]: {
  //   height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
  //   width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
  //   ...(isCollapse && {
  //     width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
  //   }),
  //   ...(isOffset && {
  //     height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
  //   }),
  //   ...(verticalLayout && {
  //     width: '100%',
  //     height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
  //     backgroundColor: theme.palette.background.default,
  //   }),
  // },
}));

// const DownloadAppAlert = styled(Alert)(({ theme }) => ({
//   fontSize: 9,
//   fontWeight: 400,
//   display: 'flex',
//   alignItems: 'center',
//   borderRadius: 0,
//   padding: '0px 16px',
//   position: 'fixed',
//   top: 0,
//   zIndex: 1,
//   width: '100%',
//   backgroundColor: '#f5b007',
// }));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  onToggleCollapse: PropTypes.func,
};

export default function DashboardHeader({
  isOpenSidebar = false,
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
  // onCloseSidebar,
  onToggleCollapse,
  showDownloadApp,
  setShowDownloadApp,
}) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const { isAuthenticated } = useAuth();

  const isDesktop = useResponsive('up', 'lg');

  const isSmall = useResponsive('down', 'md');

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const { isOpenLoginSignupDialog } = useSelector((x) => x.authPages);

  const { memberBalance } = useSelector((x) => x.lookup);

  const [openForgotPasswordDialog, setOpenForgotPasswordDialog] = useState(false);

  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);

  const navigate = useNavigate();

  const params = useParams();

  useEffect(() => {
    if (!isAuthenticated && localStorage.getItem(`referralCode`)) {
      setTimeout(() => {
        navigate('/auth/register');
        // dispatch(openLoginSignup({ open: true, isLogin: false }));
      }, 1000);
    }

    // Check if got resetpassword token
    if (localStorage.getItem(`resetToken`)) {
      setOpenResetPasswordDialog(true);
    }
  }, [params]);

  // useEffect(() => {
  //   if (isOpenLoginSignupDialog.open)
  //     setTimeout(() => {
  //       loadCaptchaEnginge(4);
  //     }, 1000);
  // }, [isOpenLoginSignupDialog]);

  return (
    <>
      {showDownloadApp && !isDesktop && (
        <DownloadAppAlert navigate={navigate} setShowDownloadApp={setShowDownloadApp} translate={translate} />
      )}
      <RootStyle
        isCollapse={isCollapse}
        isOffset={isOffset}
        verticalLayout={verticalLayout}
        showDownloadApp={showDownloadApp}
        isDesktop={isDesktop}
      >
        <Toolbar
          sx={{
            minHeight: '100% !important',
            pl: { lg: 1 },
            pr: { lg: 2 },
          }}
        >
          {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

          {!isDesktop && (
            <Stack direction="row" alignItems={'center'} sx={{ mr: 0, height: '100%' }}>
              <Box onClick={onOpenSidebar} sx={{ cursor: 'pointer' }}>
                <Image src={require('../../../assets/home/menu.png')} sx={{ height: '25px', width: '28px' }} />
              </Box>
              <Box onClick={() => navigate('/')} sx={{ ml: 1 }}>
                <Image
                  src={require('../../../assets/brand-logo.png')}
                  alt=""
                  sx={{
                    cursor: 'pointer',
                    width: 'auto',
                    height: '20px',
                    '& img': { maxHeight: '9.5vh', width: 'auto' },
                  }}
                />
              </Box>
            </Stack>
          )}

          {isDesktop && (
            <Stack direction="row" alignItems={'center'}>
              <Box
                onClick={() => onToggleCollapse()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  p: '5px',
                  mr: 2,
                  cursor: 'pointer',
                }}
              >
                <Image src={require('../../../assets/home/menu.png')} sx={{ height: '25px', width: '28px' }} />
              </Box>
              <Image
                className="header-logo"
                onClick={() => navigate('/')}
                src={require('../../../assets/brand-logo.png')}
                alt="App Logo"
                sx={{
                  height: '25px',
                  width: '109px',
                  ml: -2,
                  cursor: 'pointer',
                }}
              />
            </Stack>
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" alignItems="center" spacing={1}>
            {isDesktop && <LanguagePopover />}
            {!isAuthenticated ? (
              <>
                <Button
                  size={isSmall ? 'small' : 'medium'}
                  variant="contained"
                  onClick={() => dispatch(openLoginSignup({ open: true, isLogin: true }))}
                >
                  {translate('login')}
                </Button>
                <GoldBorderButton
                  size={isSmall ? 'small' : 'medium'}
                  onClick={() => navigate('/auth/register')}
                  wrapperProps={{
                    component: m.button,
                    animate: {
                      boxShadow: [
                        '0px 0px 5px 0px rgba(186, 186, 47,0.75)',
                        '0px 0px 15px 0px rgba(186, 186, 47,0.75)',
                        '0px 0px 5px 0px rgba(186, 186, 47,0.75)',
                      ],
                      scale: [1, 1.05, 1],
                    },
                    transition: {
                      type: 'keyframes',
                      stiffness: 260,
                      damping: 20,
                      repeat: Infinity,
                      repeatDelay: 0,
                      duration: 2.5,
                    },
                  }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {translate('sign_up')}
                </GoldBorderButton>
              </>
            ) : (
              <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center' }}>
                {isDesktop && (
                  <ShiningGoldBorder>
                    <Box
                      sx={{
                        display: 'flex',
                        borderRadius: '6px',
                        p: 0,
                        background: 'linear-gradient(0deg, #AC0202, #E00506)',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={'2px'}
                        sx={{
                          p: '5px',
                          alignItems: 'center',
                          display: 'flex',
                        }}
                      >
                        <Image
                          src={require('../../../assets/home/bitcoin.png')}
                          sx={{ width: '20px', height: '20px' }}
                        />
                        <Typography
                          onClick={() =>
                            dispatch(
                              openTransactionsDialog({
                                open: true,
                                isDeposit: true,
                              })
                            )
                          }
                          sx={{
                            cursor: 'pointer',
                            color: palette.header.balance_button.color,
                          }}
                        >
                          {`${memberBalance?.currencyCode && isDesktop ? memberBalance?.currencyCode : ''} ${
                            memberBalance?.displayBalance ? fNumber(memberBalance?.displayBalance) : '0'
                          }`}
                        </Typography>
                      </Stack>
                      <ShiningGoldBorder sx={{ m: '-2px -2px -2px 0px', height: '38px' }}>
                        <Box
                          onClick={() =>
                            dispatch(
                              openTransactionsDialog({
                                open: true,
                                isDeposit: true,
                              })
                            )
                          }
                          component={Stack}
                          direction={'row'}
                          spacing={'2px'}
                          alignItems="center"
                          sx={{
                            p: '5px',
                            cursor: 'pointer',
                            background: 'linear-gradient(0deg, #AC0202, #E00506)',
                            // margin: '-2px',
                            padding: '2px',
                            border: '1px solid gold',
                            borderRadius: 1,
                            height: '34px',
                          }}
                        >
                          <Iconify icon={'game-icons:wallet'} width={20} height={20} sx={{ color: 'white' }} />
                          <Typography
                            sx={{ color: palette.header.deposit_btn.color, display: { sm: 'unset', display: 'none' } }}
                          >
                            {translate('deposit')}
                          </Typography>
                        </Box>
                      </ShiningGoldBorder>
                    </Box>
                  </ShiningGoldBorder>
                )}
                {/* <button
              style={{
                backgroundColor: '#ff3141',
                border: 'none',
                padding: '3px 10px',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
              onClick={() =>
                dispatch(
                  openTransactionsDialog({
                    open: true,
                    isDeposit: true,
                  })
                )
              }
            >
              <Iconify icon={'fluent:wallet-24-filled'} width={20} height={20} sx={{ color: 'white' }} />
            </button> */}

                <NotificationPopover />

                <AccountPopover />
              </Stack>
            )}
          </Stack>

          {isOpenLoginSignupDialog.open && (
            <LoginSignupDialog setOpenForgotPasswordDialog={setOpenForgotPasswordDialog} />
          )}

          {openForgotPasswordDialog && (
            <ForgotPasswordDialog
              openForgotPasswordDialog={openForgotPasswordDialog}
              setOpenForgotPasswordDialog={setOpenForgotPasswordDialog}
            />
          )}

          <ResetPasswordDialog />
        </Toolbar>
      </RootStyle>
    </>
  );
}
